import React, { useState, useEffect } from 'react';
import apiClient from '../services/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { 
  MessageCircle, 
  Flag,
  Trash2,
  Loader2,
  AlertCircle,
  Search,
  Filter,
  RefreshCw,
  User,
  Calendar,
  ExternalLink,
  Eye,
  EyeOff,
  AlertTriangle,
  CheckCircle
} from 'lucide-react';
import { toast } from 'sonner';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';

const AdminCommentsSection = () => {
  const [comments, setComments] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Filters
  const [flaggedFilter, setFlaggedFilter] = useState('all');
  const [deletedFilter, setDeletedFilter] = useState('all');
  const [searchCampaign, setSearchCampaign] = useState('');
  const [searchUser, setSearchUser] = useState('');
  
  // Actions
  const [deleteCommentId, setDeleteCommentId] = useState(null);
  const [actionLoading, setActionLoading] = useState({});

  const fetchComments = async () => {
    try {
      setLoading(true);
      setError(null);
      
      let url = '/admin/comments?';
      if (flaggedFilter === 'true') url += 'flagged=true&';
      if (flaggedFilter === 'false') url += 'flagged=false&';
      if (deletedFilter === 'true') url += 'deleted=true&';
      if (deletedFilter === 'false') url += 'deleted=false&';
      if (searchCampaign) url += `campaign_id=${searchCampaign}&`;
      if (searchUser) url += `user_id_filter=${searchUser}&`;
      
      const response = await apiClient.get(url);
      setComments(response.data.comments || []);
      setStats(response.data.stats);
    } catch (err) {
      console.error('Error fetching comments:', err);
      setError('Failed to load comments');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchComments();
  }, [flaggedFilter, deletedFilter]);

  const handleFlag = async (commentId, currentlyFlagged) => {
    setActionLoading(prev => ({ ...prev, [commentId]: true }));
    try {
      await apiClient.patch(`/admin/comments/${commentId}/flag`, {
        flagged: !currentlyFlagged,
        reason: currentlyFlagged ? '' : 'Flagged by admin'
      });
      
      // Update local state
      setComments(comments.map(c => 
        c.id === commentId ? { ...c, is_flagged: !currentlyFlagged } : c
      ));
      
      toast.success(currentlyFlagged ? 'Comment unflagged' : 'Comment flagged');
    } catch (err) {
      console.error('Error flagging comment:', err);
      toast.error('Failed to flag comment');
    } finally {
      setActionLoading(prev => ({ ...prev, [commentId]: false }));
    }
  };

  const handleDelete = async () => {
    if (!deleteCommentId) return;
    
    setActionLoading(prev => ({ ...prev, [deleteCommentId]: true }));
    try {
      await apiClient.delete(`/admin/comments/${deleteCommentId}`);
      
      // Update local state
      setComments(comments.map(c => 
        c.id === deleteCommentId ? { ...c, is_deleted: true, content: '[Removed by moderator]' } : c
      ));
      
      toast.success('Comment deleted');
    } catch (err) {
      console.error('Error deleting comment:', err);
      toast.error(err.response?.data?.detail || 'Failed to delete comment');
    } finally {
      setActionLoading(prev => ({ ...prev, [deleteCommentId]: false }));
      setDeleteCommentId(null);
    }
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-center">
        <AlertCircle className="w-12 h-12 text-red-500 mb-4" />
        <p className="text-red-500 mb-4">{error}</p>
        <Button onClick={fetchComments} variant="outline">
          <RefreshCw className="w-4 h-4 mr-2" />
          Retry
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-comments-section">
      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-3 gap-4">
          <Card className="bg-card">
            <CardContent className="pt-4">
              <div className="text-center">
                <p className="text-2xl font-bold">{stats.total}</p>
                <p className="text-xs text-muted-foreground">Total Comments</p>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-yellow-500/10 border-yellow-500/30">
            <CardContent className="pt-4">
              <div className="text-center">
                <p className="text-2xl font-bold text-yellow-500">{stats.flagged}</p>
                <p className="text-xs text-muted-foreground">Flagged</p>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-red-500/10 border-red-500/30">
            <CardContent className="pt-4">
              <div className="text-center">
                <p className="text-2xl font-bold text-red-500">{stats.deleted}</p>
                <p className="text-xs text-muted-foreground">Deleted</p>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Filters */}
      <Card>
        <CardContent className="pt-4">
          <div className="flex flex-wrap gap-3 items-center">
            {/* Flagged Filter */}
            <Select value={flaggedFilter} onValueChange={setFlaggedFilter}>
              <SelectTrigger className="w-[140px]">
                <Flag className="w-4 h-4 mr-2" />
                <SelectValue placeholder="Flagged" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All</SelectItem>
                <SelectItem value="true">Flagged Only</SelectItem>
                <SelectItem value="false">Not Flagged</SelectItem>
              </SelectContent>
            </Select>
            
            {/* Deleted Filter */}
            <Select value={deletedFilter} onValueChange={setDeletedFilter}>
              <SelectTrigger className="w-[140px]">
                <Trash2 className="w-4 h-4 mr-2" />
                <SelectValue placeholder="Deleted" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All</SelectItem>
                <SelectItem value="true">Deleted Only</SelectItem>
                <SelectItem value="false">Active Only</SelectItem>
              </SelectContent>
            </Select>
            
            {/* Campaign Search */}
            <Input
              placeholder="Campaign ID..."
              value={searchCampaign}
              onChange={(e) => setSearchCampaign(e.target.value)}
              className="w-[180px]"
            />
            
            {/* User Search */}
            <Input
              placeholder="User ID..."
              value={searchUser}
              onChange={(e) => setSearchUser(e.target.value)}
              className="w-[180px]"
            />
            
            <Button variant="outline" onClick={fetchComments}>
              <Search className="w-4 h-4 mr-2" />
              Search
            </Button>
            
            <Button variant="ghost" onClick={fetchComments}>
              <RefreshCw className="w-4 h-4" />
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Comments List */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <MessageCircle className="w-5 h-5" />
            Comment Moderation
            {comments.length > 0 && (
              <Badge variant="outline">{comments.length}</Badge>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {comments.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <CheckCircle className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p>No comments found matching the criteria.</p>
            </div>
          ) : (
            <div className="space-y-4">
              {comments.map((comment) => (
                <div 
                  key={comment.id} 
                  className={`p-4 rounded-lg border ${
                    comment.is_deleted 
                      ? 'border-red-500/30 bg-red-500/5' 
                      : comment.is_flagged 
                        ? 'border-yellow-500/30 bg-yellow-500/5' 
                        : 'border-border bg-card/50'
                  }`}
                  data-testid={`admin-comment-${comment.id}`}
                >
                  <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      {/* Status badges */}
                      <div className="flex items-center gap-2 flex-wrap mb-2">
                        {comment.is_flagged && (
                          <Badge className="bg-yellow-500/20 text-yellow-400">
                            <Flag className="w-3 h-3 mr-1" />
                            Flagged
                          </Badge>
                        )}
                        {comment.is_deleted && (
                          <Badge className="bg-red-500/20 text-red-400">
                            <Trash2 className="w-3 h-3 mr-1" />
                            Deleted
                          </Badge>
                        )}
                        {comment.parent_id && (
                          <Badge variant="outline" className="text-xs">Reply</Badge>
                        )}
                      </div>
                      
                      {/* User info */}
                      <div className="flex items-center gap-2 text-sm text-muted-foreground mb-2">
                        <User className="w-3 h-3" />
                        <span className="font-medium text-foreground">{comment.user_name}</span>
                        <span className="font-mono text-xs">({comment.user_id?.substring(0, 8)}...)</span>
                        <span>•</span>
                        <Calendar className="w-3 h-3" />
                        <span>{formatDate(comment.created_at)}</span>
                      </div>
                      
                      {/* Content */}
                      <p className={`text-sm ${comment.is_deleted ? 'text-muted-foreground italic' : ''}`}>
                        {comment.content}
                      </p>
                      
                      {/* Original content for deleted */}
                      {comment.is_deleted && comment.original_content && (
                        <p className="text-xs text-muted-foreground mt-2 p-2 bg-muted/30 rounded">
                          Original: "{comment.original_content.substring(0, 200)}..."
                        </p>
                      )}
                      
                      {/* Campaign link */}
                      <div className="flex items-center gap-2 mt-2 text-xs text-muted-foreground">
                        <span>Campaign:</span>
                        <a 
                          href={`/campaign/${comment.campaign_id}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="font-mono text-primary hover:underline flex items-center gap-1"
                        >
                          {comment.campaign_id?.substring(0, 12)}...
                          <ExternalLink className="w-3 h-3" />
                        </a>
                      </div>
                    </div>
                    
                    {/* Actions */}
                    <div className="flex gap-2 flex-shrink-0">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleFlag(comment.id, comment.is_flagged)}
                        disabled={actionLoading[comment.id]}
                        className={comment.is_flagged ? 'text-yellow-500' : ''}
                        data-testid={`flag-comment-${comment.id}`}
                      >
                        {actionLoading[comment.id] ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <Flag className="w-4 h-4 mr-1" />
                        )}
                        {comment.is_flagged ? 'Unflag' : 'Flag'}
                      </Button>
                      
                      {!comment.is_deleted && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setDeleteCommentId(comment.id)}
                          disabled={actionLoading[comment.id]}
                          className="text-red-500 hover:bg-red-500/10"
                          data-testid={`delete-comment-${comment.id}`}
                        >
                          <Trash2 className="w-4 h-4 mr-1" />
                          Delete
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Delete Confirmation */}
      <AlertDialog open={!!deleteCommentId} onOpenChange={() => setDeleteCommentId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-red-500" />
              Delete Comment
            </AlertDialogTitle>
            <AlertDialogDescription>
              This will soft delete the comment and show "[Removed by moderator]" in its place. 
              The original content will be preserved for audit purposes.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleDelete}
              className="bg-red-600 hover:bg-red-700"
            >
              Delete Comment
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default AdminCommentsSection;
